/**
 * The AI parts of the app: insights (what's different from normal, worked out on the server
 * from each port's own history) and Ask AI (questions about the farm, answered from its data).
 * Both only look: switching equipment or changing set-up always needs a person.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { api, ApiError, type AskMessage, type Insight } from "../api.ts";
import { go } from "../route.ts";
import { Icon, Section, useLoad } from "../ui.tsx";
import { LockCard, useCan } from "../plan.tsx";

export function useInsights(accountId: string | undefined, staff: boolean) {
  return useLoad(() => (staff && !accountId ? Promise.resolve({ insights: [] as Insight[] }) : api.insights(staff ? accountId : undefined)), [accountId, staff], 10 * 60_000);
}

/** Insights as a list; on a device page only that device's. */
export function InsightList({ insights, devEui, max }: { insights: Insight[]; devEui?: string; max?: number }) {
  const list = insights.filter((i) => !devEui || i.devEui === devEui).slice(0, max ?? 50);
  if (!list.length) return null;
  return (
    <div className="card list">
      {list.map((i) => (
        <div key={i.id} className="alert">
          <span className={`sev ${i.severity}`} aria-label={i.severity === "warn" ? "Check soon" : "For your information"}>
            <Icon name="spark" size={16} />
          </span>
          <div style={{ minWidth: 0 }}>
            <h3>
              {devEui ? (
                i.title
              ) : (
                <button className="linkish" onClick={() => go({ name: "device", eui: i.devEui })}>
                  {i.title}
                </button>
              )}
            </h3>
            <p>{i.detail}</p>
            {!devEui && <p className="small">{i.deviceName}</p>}
          </div>
          <span />
        </div>
      ))}
    </div>
  );
}

/** Farm home card: insights, and the way into Ask AI. */
export function InsightsCard({ accountId, staff }: { accountId: string | undefined; staff: boolean }) {
  const allowed = useCan("insights");
  const insights = useInsights(accountId, staff);
  const list = insights.data?.insights ?? [];
  if (!allowed) {
    return (
      <Section title="Insights">
        <LockCard feature="insights" />
      </Section>
    );
  }
  return (
    <Section
      title="Insights"
      right={
        <button className="btn ghost" onClick={() => go({ name: "ask" })}>
          <Icon name="spark" size={16} /> Ask AI
        </button>
      }
    >
      {list.length ? (
        <InsightList insights={list} max={5} />
      ) : (
        <div className="card empty small">
          {insights.data ? "Nothing out of the ordinary in the last 24 hours compared with the two weeks before." : "Looking at the last two weeks…"}
        </div>
      )}
    </Section>
  );
}

const FARM_STARTERS = ["How full are my tanks?", "Did any pumps run longer than usual this week?", "Is anything offline or needing a look?", "How much did the bore pump run yesterday?"];
const HELP_STARTERS = ["How do I switch a pump on?", "How do I get alerts on my phone?", "What can I do with my role?", "How do I set up a tank alert?"];
const STORE_KEY = "tc.ask";

/** The full-page Ask AI (farm data needs Ask AI on the plan; how-to help is in the help button). */
export function AskAi({ accountId, staff }: { accountId: string | undefined; staff: boolean }) {
  const allowed = useCan("ask-ai");
  if (!allowed) return <AskAiLocked />;
  return (
    <Section title="Ask AI">
      <p className="muted small" style={{ margin: 0 }}>
        Ask about your farm in plain words. Answers come from your devices' own readings, alerts and programs. The AI can only look: it can't switch
        pumps or change set-up, and it can be wrong, so check anything important on the device page.
      </p>
      <Chat accountId={accountId} staff={staff} screen="Ask AI" />
    </Section>
  );
}

function AskAiLocked() {
  return (
    <Section title="Ask AI">
      <LockCard feature="ask-ai">
        <div className="card chat" style={{ minHeight: 200 }}>
          <div className="bubble user">How full are my tanks?</div>
          <div className="bubble assistant">Tank 1 is 52 % full and dropping about 5 % an hour; Header tank is 70 % and steady.</div>
        </div>
      </LockCard>
    </Section>
  );
}

/**
 * One conversation per farm for this browser tab, shared by the Ask AI page and the help panel,
 * so a question started in one carries on in the other.
 */
export function Chat({
  accountId,
  staff,
  screen,
  onNavigate,
  onTour,
}: {
  accountId: string | undefined;
  staff: boolean;
  /** The screen they're on, so "what's this?" makes sense. */
  screen?: string;
  /** Called after a link in an answer is followed (the help panel closes on a phone). */
  onNavigate?: () => void;
  /** "Show me around" starts the tour instead of asking. */
  onTour?: () => void;
}) {
  const status = useLoad(() => api.aiStatus(staff ? accountId : undefined), [accountId, staff]);
  const key = `${STORE_KEY}.${accountId ?? "mine"}`;
  const [messages, setMessages] = useState<AskMessage[]>(() => {
    try {
      return JSON.parse(sessionStorage.getItem(key) ?? "[]") as AskMessage[];
    } catch {
      return [];
    }
  });
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const end = useRef<HTMLDivElement>(null);
  useEffect(() => {
    try {
      sessionStorage.setItem(key, JSON.stringify(messages.slice(-30)));
    } catch {
      // Private windows may refuse storage; the chat still works.
    }
    end.current?.scrollIntoView({ block: "end" });
  }, [messages, key]);

  const send = async (q: string) => {
    const question = q.trim();
    if (!question || busy) return;
    const next: AskMessage[] = [...messages, { role: "user", text: question }];
    setMessages(next);
    setText("");
    setBusy(true);
    setError("");
    try {
      const r = await api.ask(next, staff ? accountId : undefined, screen);
      setMessages([...next, { role: "assistant", text: r.answer }]);
    } catch (e) {
      setError(e instanceof ApiError || e instanceof Error ? e.message : "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  const off = status.data && !status.data.ask;
  const starters = [...HELP_STARTERS.slice(0, status.data?.farmData ? 2 : 4), ...(status.data?.farmData ? FARM_STARTERS.slice(0, 2) : [])];
  return (
    <>
      {off && <div className="banner info small">The assistant isn't switched on for this server yet. FMS needs to add an AI key.</div>}
      <div className="card chat">
        {!messages.length && (
          <div className="chat-start">
            <Icon name="spark" size={28} />
            <p>{status.data?.farmData ? "Ask how to do something in the app, or about your farm." : "Ask how to do something in the app."}</p>
            <div className="chips">
              {onTour && (
                <button className="chip" onClick={onTour}>
                  Show me around
                </button>
              )}
              {starters.map((s) => (
                <button key={s} className="chip" disabled={!!off || busy} onClick={() => void send(s)}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`bubble ${m.role}`}>
            {m.role === "assistant" ? <Answer text={m.text} onNavigate={onNavigate} /> : m.text}
          </div>
        ))}
        {busy && <div className="bubble assistant muted">Looking that up…</div>}
        {error && <div className="error small">{error}</div>}
        <div ref={end} />
      </div>
      <form
        className="ask-bar"
        onSubmit={(e) => {
          e.preventDefault();
          void send(text);
        }}
      >
        <input
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, 1000))}
          placeholder={status.data?.farmData ? "How do I…, or ask about your tanks, pumps…" : "How do I…"}
          aria-label="Your question"
          disabled={!!off}
        />
        <button className="btn primary" disabled={!text.trim() || busy || !!off}>
          Ask
        </button>
      </form>
      {messages.length > 0 && (
        <button className="btn ghost small" style={{ alignSelf: "flex-start" }} onClick={() => setMessages([])}>
          New conversation
        </button>
      )}
    </>
  );
}

/**
 * An answer, with the app's own screen links ([Alerts](#/alerts)) as buttons and **bold** kept.
 * Only links into the app are followed; anything else stays plain text.
 */
export function Answer({ text, onNavigate }: { text: string; onNavigate?: () => void }) {
  const out: ReactNode[] = [];
  const re = /\[([^\]\n]{1,60})\]\((#\/[\w/-]*)\)|\*\*([^*\n]{1,200})\*\*/g;
  let last = 0;
  let n = 0;
  for (let m = re.exec(text); m; m = re.exec(text)) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[1] && m[2]) {
      const link = m[2];
      out.push(
        <a
          key={n++}
          className="answer-link"
          href={link}
          onClick={() => onNavigate?.()}
        >
          {m[1]}
        </a>,
      );
    } else out.push(<b key={n++}>{m[3]}</b>);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return <>{out}</>;
}
